import React, { useState } from 'react';
import { Alert, Input, Modal, Typography } from 'antd';

const { Text } = Typography;

interface DeletePendingOnboardingModalProps {
  // e.g. "Azure" or "Microsoft 365"
  kind: string;
  companyName: string | null;
  buName: string | null;
  // Resolves true when the delete succeeded (the parent then unmounts this modal).
  onConfirm: () => Promise<boolean>;
  onClose: () => void;
}

// Confirms deleting an onboarding request the client never completed (e.g. they decided not to
// onboard). The server only allows it for entries with no history; this is the type-to-confirm
// guard on top, the same pattern as Reset tenant.
const DeletePendingOnboardingModal: React.FC<DeletePendingOnboardingModalProps> = ({
  kind,
  companyName,
  buName,
  onConfirm,
  onClose,
}) => {
  const [typed, setTyped] = useState('');
  const [saving, setSaving] = useState(false);
  // bu_name is nullable in the lists; fall back to a fixed word so the guard still works.
  const confirmWord = buName || 'DELETE';

  const handleOk = async () => {
    setSaving(true);
    if (!(await onConfirm())) setSaving(false);
  };

  return (
    <Modal
      wrapClassName="custom-modal"
      title={`Delete ${kind} onboarding request?`}
      centered
      visible
      onCancel={onClose}
      onOk={handleOk}
      okText="Delete"
      okButtonProps={{ danger: true, disabled: typed.trim() !== confirmWord, loading: saving }}
      cancelText="Cancel"
      width={560}
    >
      <Alert
        type="warning"
        showIcon
        message={
          <>
            This removes the pending {kind} onboarding request for{' '}
            <em>
              {companyName ?? '–'} / {buName ?? '–'}
            </em>
            .
          </>
        }
        style={{ marginBottom: 12 }}
      />
      <ul style={{ paddingLeft: 20 }}>
        <li>Use it when the client has decided not to onboard.</li>
        <li>The request, its client link and its unused collection schedule are removed.</li>
        <li>No data has been collected for it, so nothing collected is lost.</li>
        <li>To onboard this client later, create a new request.</li>
      </ul>
      <div className="form-group m-0">
        Type <Text code>{confirmWord}</Text> to confirm
        <Input
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          aria-label="Confirm delete"
          autoComplete="off"
        />
      </div>
    </Modal>
  );
};

export default DeletePendingOnboardingModal;
